import CustomButton from "@components/buttons/CustomButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import Checkbox from "@components/inputs/CheckBox";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { settleAccountApiServices } from "@services/apis/SettleAccounts";
import { GetCryptoCurrencyListData } from "@services/models/Transactions/GetCryptoCurrencyListModel";
import { useEffect, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import SettledAccountReponseSlider from "../../settleaccounts/components/SettledAccountReponseSlider";

type AccountType =
  | "user-id"
  | "user-email"
  | "merchant-id"
  | "merchant-name"
  | "merchant-tylt";

interface TransferItem {
  fromType: AccountType | "";
  fromId: string;
  toType: AccountType | "";
  toId: string;
  amount: string;
  currencySymbol: string;
  notes: string;
}

interface ApiTransferData {
  from: {
    type: string;
    userId?: string;
    merchantId?: string;
    emailId?: string;
    merchantName?: string;
  };
  to: {
    type: string;
    userId?: string;
    merchantId?: string;
    emailId?: string;
    merchantName?: string;
  };
  currencySymbol: string;
  amount: string;
  notes: string;
}

interface ApiPayload {
  transferData: ApiTransferData[];
  settleTradeId: string | undefined;
  manualSettlement: boolean;
  cashierUTR: string | undefined;
  isManuallySettled?: boolean;
  sendWebhook: boolean;
}

const accountTypes: AccountType[] = [
  "user-id",
  "user-email",
  "merchant-id",
  "merchant-name",
  "merchant-tylt",
];

const MobileTransfer = ({
  index,
  transfer,
  updateTransfer,
  removeTransfer,
  tokens,
}: {
  index: number;
  transfer: TransferItem;
  updateTransfer: (index: number, transfer: TransferItem) => void;
  removeTransfer: (index: number) => void;
  tokens: GetCryptoCurrencyListData[];
}) => {
  const handleChange = (field: keyof TransferItem, value: string) => {
    updateTransfer(index, { ...transfer, [field]: value });
  };

  const handleFromTypeChange = (type: AccountType | "") => {
    const updatedTransfer = { ...transfer, fromType: type };
    if (type === "merchant-tylt") {
      updatedTransfer.fromId = "450";
    }
    updateTransfer(index, updatedTransfer);
  };

  const handleToTypeChange = (type: AccountType | "") => {
    const updatedTransfer = { ...transfer, toType: type };
    if (type === "merchant-tylt") {
      updatedTransfer.toId = "450";
    }
    updateTransfer(index, updatedTransfer);
  };

  const handleTokenChange = (token: string) => {
    handleChange("currencySymbol", token);
  };

  const inputStyle =
    "py-1 px-2 h-8 text-xs border border-border-subtle rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-full";
  const selectStyle =
    "h-8 text-xs border border-border-subtle rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-full";

  return (
    <div className="flex flex-col bg-bg-base px-3 py-3 border-b border-border-subtle gap-2">
      <div className="flex flex-row items-center justify-between">
        <span className="font-semibold text-[11px] text-text-base">
          Transfer #{index + 1}
        </span>
        <div
          className="h-[20px] w-[20px] rounded-[2px] bg-bg-status-red flex items-center justify-center cursor-pointer"
          onClick={() => removeTransfer(index)}
        >
          <span className="material-symbols-outlined text-[14px] text-text-base">
            close
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          <div className="w-1/2">
            <label className="text-[10px] font-semibold text-text-label mb-1 block">
              From
            </label>
            <select
              className={selectStyle}
              value={transfer.fromType}
              onChange={(e) =>
                handleFromTypeChange(e.target.value as AccountType | "")
              }
            >
              <option value="" disabled>
                Select
              </option>
              {accountTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="w-1/2">
            <label className="text-[10px] font-semibold text-text-label mb-1 block">
              From ID
            </label>
            <input
              className={inputStyle}
              placeholder="Enter Id/Email/Name"
              value={transfer.fromId}
              onChange={(e) => handleChange("fromId", e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2">
          <div className="w-1/2">
            <label className="text-[10px] font-semibold text-text-label mb-1 block">
              To
            </label>
            <select
              className={selectStyle}
              value={transfer.toType}
              onChange={(e) =>
                handleToTypeChange(e.target.value as AccountType | "")
              }
            >
              <option value="" disabled>
                Select
              </option>
              {accountTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="w-1/2">
            <label className="text-[10px] font-semibold text-text-label mb-1 block">
              To ID
            </label>
            <input
              className={inputStyle}
              placeholder="Enter Id/Email/Name"
              value={transfer.toId}
              onChange={(e) => handleChange("toId", e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2">
          <div className="w-1/2">
            <label className="text-[10px] font-semibold text-text-label mb-1 block">
              Amount
            </label>
            <input
              className={inputStyle}
              placeholder="Enter Amount"
              value={transfer.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
            />
          </div>
          <div className="w-1/2">
            <label className="text-[10px] font-semibold text-text-label mb-1 block">
              Currency
            </label>
            <select
              className={selectStyle}
              value={transfer.currencySymbol}
              onChange={(e) => handleTokenChange(e.target.value)}
            >
              <option value="" disabled>
                Select
              </option>
              {tokens.map((token) => (
                <option
                  key={token.cryptoCurrencyId}
                  value={token.cryptoCurrencySymbol}
                >
                  {token.cryptoCurrencySymbol}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="w-full">
          <label className="text-[10px] font-semibold text-text-label mb-1 block">
            Notes
          </label>
          <input
            className={inputStyle}
            placeholder="Enter Notes"
            value={transfer.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
          />
          <div className="flex space-x-2 mt-1">
            {["Manual Settlement: TID:", "LOAN. Manual Settlement: TID:"].map(
              (noteText, idx) => (
                <div
                  key={idx}
                  onClick={() => handleChange("notes", noteText)}
                  className="cursor-pointer border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] border-border-subtle bg-bg-page-body"
                >
                  {idx === 0 ? "Manual Settlement" : "LOAN"}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MobileSettleAccounts = () => {
  const [transfers, setTransfers] = useState<TransferItem[]>([]);
  const [settleTradeCheckBox, setSettleTradeCheckBox] =
    useState<boolean>(false);
  const [sendWebhookCheckBox, setSendWebhookCheckBox] =
    useState<boolean>(false);
  const [manualCheckBox, setManualCheckBox] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [tradeId, setTradeId] = useState<string>("");
  const [cashierUtr, setCashierUtr] = useState<string>("");

  const { handleError, handleSuccess } = useSnackbarMessage();

  const [settledAccountReponseSlider, setSettledAccountReponseSlider] =
    useState(false);

  const [tokens, setTokens] = useState<GetCryptoCurrencyListData[]>([]);

  const getTokens = async () => {
    try {
      const tokenResponse =
        await settleAccountApiServices.getCryptoCurrencyList();
      setTokens(tokenResponse.data);
    } catch (error) {}
  };

  useEffect(() => {
    getTokens();
  }, []);

  const addTransfer = () => {
    setTransfers([
      ...transfers,
      {
        fromType: "",
        fromId: "",
        toType: "",
        toId: "",
        amount: "",
        currencySymbol: "",
        notes: "",
      },
    ]);
  };

  const updateTransfer = (index: number, updatedTransfer: TransferItem) => {
    const newTransfers = [...transfers];
    newTransfers[index] = updatedTransfer;
    setTransfers(newTransfers);
  };

  const removeTransfer = (index: number) => {
    const newTransfers = [...transfers];
    newTransfers.splice(index, 1);
    setTransfers(newTransfers);
  };

  const validateTransfers = (): boolean => {
    for (const transfer of transfers) {
      if (
        !transfer.fromType.trim() ||
        !transfer.fromId.trim() ||
        !transfer.toType.trim() ||
        !transfer.toId.trim() ||
        !transfer.amount.trim() ||
        !transfer.currencySymbol.trim() ||
        !transfer.notes.trim()
      ) {
        return false;
      }
    }
    if (settleTradeCheckBox && !tradeId.trim()) {
      return false;
    }
    return transfers.length > 0;
  };

  const formatDataForApi = (): ApiPayload => {
    const transferData: ApiTransferData[] = transfers.map((transfer) => {
      const fromType =
        transfer.fromType === "merchant-tylt"
          ? "merchant-id"
          : transfer.fromType.trim();
      const toType =
        transfer.toType === "merchant-tylt"
          ? "merchant-id"
          : transfer.toType.trim();

      const formattedTransfer: ApiTransferData = {
        from: {
          type: fromType,
        },
        to: {
          type: toType,
        },
        currencySymbol: transfer.currencySymbol.trim(),
        amount: transfer.amount.trim(),
        notes: transfer.notes.trim(),
      };

      if (transfer.fromType === "user-id") {
        formattedTransfer.from.userId = transfer.fromId.trim();
      } else if (
        transfer.fromType === "merchant-id" ||
        transfer.fromType === "merchant-tylt"
      ) {
        formattedTransfer.from.merchantId = transfer.fromId.trim();
      } else if (transfer.fromType === "user-email") {
        formattedTransfer.from.emailId = transfer.fromId.trim();
      } else {
        formattedTransfer.from.merchantName = transfer.fromId.trim();
      }

      if (transfer.toType === "user-id") {
        formattedTransfer.to.userId = transfer.toId.trim();
      } else if (
        transfer.toType === "merchant-id" ||
        transfer.toType === "merchant-tylt"
      ) {
        formattedTransfer.to.merchantId = transfer.toId.trim();
      } else if (transfer.toType === "user-email") {
        formattedTransfer.to.emailId = transfer.toId.trim();
      } else {
        formattedTransfer.to.merchantName = transfer.toId.trim();
      }

      return formattedTransfer;
    });

    return {
      transferData,
      settleTradeId: settleTradeCheckBox ? tradeId.trim() : undefined,
      manualSettlement: settleTradeCheckBox && manualCheckBox,
      cashierUTR:
        settleTradeCheckBox && cashierUtr ? cashierUtr.trim() : undefined,
      sendWebhook: sendWebhookCheckBox,
    };
  };

  const [responseData, setResponseData] = useState<any>({});
  const [responseIsSuccess, setResponseIsSuccess] = useState<boolean>(true);
  const [responseErrorMessage, setResponseErrorMessage] = useState<string>("");

  const handleSubmit = async () => {
    if (!validateTransfers()) {
      handleError({ message: "Please fill in all required fields" });
      return;
    }

    setSubmitLoading(true);
    try {
      const formattedData = formatDataForApi();
      const response = await settleAccountApiServices.settleAccounts(
        formattedData
      );
      setResponseData(response);
      setResponseIsSuccess(true);
      setResponseErrorMessage("");
      handleSuccess({ message: "Accounts settled successfully" });
      setTransfers([]);
      setSettleTradeCheckBox(false);
      setSendWebhookCheckBox(false);
      setTradeId("");
    } catch (error: any) {
      setResponseData(
        error.response?.data || { error: error.response.data.msg }
      );
      setResponseIsSuccess(false);
      setResponseErrorMessage(
        error.response.data.msg || "Failed to settle accounts"
      );
      handleError({ message: error.data.msg || "Failed to settle accounts" });
    } finally {
      setSubmitLoading(false);
      setSettledAccountReponseSlider(true);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen">
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row items-center px-4 sticky top-0 bg-white z-10 w-full">
        <p className="text-text-base text-[14px] font-semibold">
          Prime: Settle Accounts
        </p>
      </div>

      <div className="flex flex-col pb-[80px]">
        <div className="px-3 py-3 space-y-3">
          <LightCustomButton
            text="+ ADD TRANSFER"
            isActive={true}
            onClick={addTransfer}
            className="w-full"
          />
        </div>

        <div className="flex flex-col border-t border-border-subtle">
          {transfers.map((transfer, index) => (
            <MobileTransfer
              key={index}
              index={index}
              transfer={transfer}
              updateTransfer={updateTransfer}
              removeTransfer={removeTransfer}
              tokens={tokens}
            />
          ))}

          {transfers.length === 0 && (
            <div className="flex justify-center items-center py-8 text-[#BEBEBE]">
              <span className="font-normal text-[10px]">
                Click "+ ADD TRANSFER" to begin
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col space-y-3 px-3 py-3">
          <div className="flex flex-col py-2 px-3 space-y-4 border border-border-subtle bg-bg-base rounded-lg">
            <div className="flex flex-row items-center justify-between">
              <span className="text-text-base text-[12px] font-normal">
                Settle Trade?
              </span>
              <Checkbox
                checked={settleTradeCheckBox}
                onChange={() => {
                  setSettleTradeCheckBox(!settleTradeCheckBox);
                }}
              />
            </div>
            {settleTradeCheckBox && (
              <GlobalTextInputField
                placeholder="Enter Trade ID"
                value={tradeId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setTradeId(e.target.value)
                }
              />
            )}
            {settleTradeCheckBox && (
              <GlobalTextInputField
                placeholder="Enter Cashier UTR"
                value={cashierUtr}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCashierUtr(e.target.value)
                }
              />
            )}

            {settleTradeCheckBox && (
              <div className="flex flex-row items-center justify-between">
                <span className="text-text-base text-[12px] font-normal">
                  Manual settlement
                </span>
                <Checkbox
                  checked={manualCheckBox}
                  onChange={() => {
                    setManualCheckBox(!manualCheckBox);
                  }}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col py-2 px-3 space-y-3 border border-border-subtle bg-bg-base rounded-lg">
            <div className="flex flex-row items-center justify-between">
              <span className="text-text-base text-[12px] font-normal">
                Send Webhook?
              </span>
              <Checkbox
                checked={sendWebhookCheckBox}
                onChange={() => {
                  setSendWebhookCheckBox(!sendWebhookCheckBox);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10 p-4 bg-white border-t border-border-subtle">
        <CustomButton
          text="SETTLE ACCOUNTS"
          onClick={handleSubmit}
          isActive={validateTransfers()}
          isButtonLoading={submitLoading}
        />
      </div>

      <SettledAccountReponseSlider
        isOpen={settledAccountReponseSlider}
        onClose={() => setSettledAccountReponseSlider(false)}
        data={responseData}
        isSuccess={responseIsSuccess}
        errorMessage={responseErrorMessage}
      />
    </div>
  );
};

export default MobileSettleAccounts;
